import moment from 'moment'
import { useEffect as ReactUseEffect, useMemo,useState as ReactUseState  } from 'react'

import { generateCacheKey } from '../../utils/cache'

/**
 * @typedef FeatureToggle
 * @property {string} id
 * @property {string} key
 * @property {string} start_from
 * @property {string} stop_from
 * @property {string} updated_by
 * @property {string} updated_at
 * @property {string} created_at
 * @property {string} tag
 */

/**
 * @typedef {object} UseFeatureToggleStoreProps
 * @property {import('../../utils/env').Region} region
 * @property {import('../../utils/env').Env} env
 * @property {() => Promise<import('axios').AxiosResponse<{ features: FeatureToggle[] }>>} getFeatureToggles
 * @property {Function} useRequest
 * @property {() => void} clearOnMessageHandlers
 * @property {(event: string, handler: () => void) => void} addOnMessageHandler
 * @property {boolean} isSocketReady
 * @property {{ useState?: typeof ReactUseState, useEffect?: typeof ReactUseEffect }} [customReactHooks]
 */

/**
 * @typedef {object} FeatureToggleStore
 * @property {(key: string) => boolean} isEnabled
 * @property {boolean} isReady
 */

/**
 * @param {UseFeatureToggleStoreProps} props
 * @returns {FeatureToggleStore}
 */
export const useFeatureToggleStore = ({ region, env, getFeatureToggles, useRequest, clearOnMessageHandlers, addOnMessageHandler, isSocketReady, customReactHooks = {} }) => {
  const {
    useState = ReactUseState,
    useEffect = ReactUseEffect,
  } = customReactHooks

  /** @type {ReturnType<typeof import('react').useState<{ [feature: string]: FeatureToggle }>>} */
  const [localCopy, setLocalCopy] = useState({})
  const isReady = useMemo(
    () => Object.keys(localCopy).length > 0,
    [localCopy],
  )

  /** @type { { request: () => Promise<import('axios').AxiosResponse<FeatureToggle[]>>, data: FeatureToggle[]}} */
  const {
    request: fetchFeatureToggles,
    data: featureData,
  } = useRequest(getFeatureToggles, {
    initialData: [],
    transformData: ({ features }) => features,
  })

  /** @param {string} key */
  const isEnabled = (key) => {
    const cachedKey = generateCacheKey('region', 'env')({ region, env }, { key })
    if (!(cachedKey in localCopy)) return false

    const feature = localCopy[cachedKey]
    if (!feature) return false

    if (moment(feature.start_from).isBefore(moment()) && (!feature.stop_from || moment(feature.stop_from).isAfter(moment()))) {
      return true
    }
    return false
  }

  useEffect(() => {
    return () => {
      clearOnMessageHandlers()
    }
  }, [])

  useEffect(() => {
    if (isSocketReady) {
      fetchFeatureToggles({ tag: 'developing' })
      addOnMessageHandler(`${region}.${env}.setting.FeatureToggles.CreateFeature`, () => fetchFeatureToggles({ tag: 'developing' }))
      addOnMessageHandler(`${region}.${env}.setting.FeatureToggles.UpdateFeature`, () => fetchFeatureToggles({ tag: 'developing' }))
      addOnMessageHandler(`${region}.${env}.setting.FeatureToggles.DeleteFeature`, () => fetchFeatureToggles({ tag: 'developing' }))
    }
  }, [isSocketReady])

  useEffect(() => {
    const newLocalCopy = Object.fromEntries(featureData.map((feat) => {
      return [generateCacheKey('region', 'env')({ env, region }, { key: feat.key }), feat]
    }))
    setLocalCopy((prev) => ({
      ...prev,
      ...newLocalCopy,
    }))
  }, [featureData])

  return { isEnabled, isReady }
}
